import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProject, useUpdateProject } from "@/hooks/useProjects";
import { AppHeader } from "@/components/AppHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/lib/toast";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Save, X } from "lucide-react";
import type { UpdateProjectRequest } from "@/types/api";

export default function EditProject() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: project, isLoading, error } = useProject(id!);
  const updateProject = useUpdateProject();

  const [form, setForm] = useState<UpdateProjectRequest>({
    name: "",
    description: "",
    ict_responsible: "",
    start_date: "",
    end_date: "",
    go_live_date: "",
  });

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name,
        description: project.description ?? "",
        ict_responsible: project.ict_responsible,
        start_date: project.start_date?.split("T")[0] ?? "",
        end_date: project.end_date?.split("T")[0] ?? "",
        go_live_date: project.go_live_date?.split("T")[0] ?? "",
      });
    }
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name?.trim() || !form.ict_responsible?.trim()) {
      toast.error("Name and ICT Responsible are required.");
      return;
    }

    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("End date must be on or after the start date.");
      return;
    }
    if (form.end_date && form.go_live_date && form.go_live_date < form.end_date) {
      toast.error("Go-live date must be on or after the end date.");
      return;
    }

    const updates: UpdateProjectRequest = {
      name: form.name.trim(),
      description: form.description?.trim() ?? "",
      ict_responsible: form.ict_responsible.trim(),
      start_date: form.start_date ?? "",
      end_date: form.end_date ?? "",
      go_live_date: form.go_live_date ?? "",
    };

    await toast.promise(
      updateProject.mutateAsync({ projectId: id!, updates }),
      {
        loading: "Saving project...",
        success: "Project updated successfully",
        error: (err: Error) => `Failed to update project: ${err.message}`,
      }
    );

    navigate("/admin/projects");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader title="Edit Project" showBack backPath="/admin/projects" />
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader title="Edit Project" showBack backPath="/admin/projects" />
        <div className="max-w-3xl mx-auto px-6 py-10">
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              {error ? `Error loading project: ${(error as Error).message}` : "Project not found."}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const statusConfig: Record<string, { label: string; color: string }> = {
    active: { label: "Active", color: "bg-info/10 text-info border-info/20" },
    inactive: { label: "Inactive", color: "bg-muted text-muted-foreground border-muted" },
  };

  const status = statusConfig[project.status] ?? statusConfig.active;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="Edit Project" showBack backPath="/admin/projects" />

      <main className="max-w-3xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Read-only info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Project Details</CardTitle>
                <Badge variant="outline" className={status.color}>
                  {status.label}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="project_number">Project Number</Label>
                  <Input
                    id="project_number"
                    value={project.project_number}
                    disabled
                    className="bg-muted"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="testing_phase">Testing Phase</Label>
                  <Input
                    id="testing_phase"
                    value={project.testing_phase}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Editable fields */}
          <Card>
            <CardHeader>
              <CardTitle>General Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ict_responsible">
                  ICT Responsible <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="ict_responsible"
                  value={form.ict_responsible}
                  onChange={(e) => setForm((f) => ({ ...f, ict_responsible: e.target.value }))}
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="go_live_date">Go-Live Date</Label>
                  <Input
                    id="go_live_date"
                    type="date"
                    value={form.go_live_date}
                    onChange={(e) => setForm((f) => ({ ...f, go_live_date: e.target.value }))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/projects")}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button type="submit" disabled={updateProject.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {updateProject.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
